/**
 * Read your plan (Regina, 10/28; server: routes/jobTechPlan.ts, migration 703).
 *
 * City of Arlington Airport: the plan said a 6" replacement when the work was
 * packing and bolts, and nobody asked before leaving the shop. The tech's
 * "What you'll do today" block now ends with one big Got it and a "This looks
 * wrong · call the office". The office sees who read it and when, on the job's
 * "What the tech will do" block. A "looks wrong" also lands on the Office list.
 * No bell, no push.
 *
 * Shown where the tech already looks first (his job page), never as a pop-up.
 * If the office changes the plan after he read it, he is asked again.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { InfoButton } from "@/components/info-button";
import { TECH_PLAN_HELP } from "./tech-plan-help";

export interface PlanRead {
    user_id: string;
    name: string | null;
    verdict: "got_it" | "looks_wrong";
    note: string | null;
    created_at: string;
    /** The office changed the plan after this read. */
    stale: boolean;
}

export interface PlanReadsResponse {
    reads: PlanRead[];
    mine: PlanRead | null;
    officePhone: string | null;
}

export const planReadsKey = (jobId: string) => ["job", jobId, "tech-plan", "reads"] as const;

export function usePlanReads(jobId: string | null | undefined) {
    return useQuery({
        queryKey: planReadsKey(jobId ?? ""),
        queryFn: () => api.get<PlanReadsResponse>(`/api/jobs/${jobId}/tech-plan/reads`),
        enabled: !!jobId,
        staleTime: 30_000,
    });
}

/** "7:42 AM" today, "10/27 7:42 AM" another day. */
export function readTimeWords(iso: string, now: Date = new Date()): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay ? time : `${d.getMonth() + 1}/${d.getDate()} ${time}`;
}

function telHref(phone: string): string {
    return `tel:${phone.replace(/[^\d+]/g, "")}`;
}

/** The tech's side: under "What you'll do today" on his job page. */
export function ReadYourPlan({ jobId }: { jobId: string }) {
    const qc = useQueryClient();
    const q = usePlanReads(jobId);
    const [wrongOpen, setWrongOpen] = useState(false);
    const [note, setNote] = useState("");
    const send = useMutation({
        mutationFn: (body: { verdict: "got_it" | "looks_wrong"; note?: string }) =>
            api.post<{ ok: true; mine: PlanRead | null }>(`/api/jobs/${jobId}/tech-plan/read`, body),
        onSuccess: (_r, body) => {
            setWrongOpen(false);
            setNote("");
            void qc.invalidateQueries({ queryKey: planReadsKey(jobId) });
            toast.success(body.verdict === "got_it" ? "Got it. The office sees you read it." : "Sent to the office.");
        },
        onError: (e: unknown) =>
            toast.error(
                (e as { body?: { message?: string } })?.body?.message ??
                    "That didn't save. Check your signal and try again.",
            ),
    });
    if (q.isLoading || q.isError || !q.data) return null;
    const mine = q.data.mine;
    const phone = q.data.officePhone;
    const callOffice = phone ? (
        <a
            href={telHref(phone)}
            className="inline-flex min-h-[44px] items-center font-semibold text-blue-800 underline"
            data-testid="plan-call-office"
        >
            Call the office · {phone}
        </a>
    ) : (
        <span className="text-stone-600">Call the office.</span>
    );

    if (mine && !mine.stale && mine.verdict === "got_it") {
        return (
            <p
                className="rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-sm font-semibold text-green-900"
                data-testid="plan-read-done"
            >
                ✓ You read the plan · {readTimeWords(mine.created_at)}
            </p>
        );
    }

    return (
        <section
            aria-label="Read your plan"
            className="space-y-2.5 rounded-xl border-2 border-[#7a5c0a] bg-white p-4 text-stone-900"
            data-testid="read-your-plan"
        >
            <div className="flex items-center gap-2">
                <h3 className="text-base font-extrabold">Read your plan</h3>
                <InfoButton topic={TECH_PLAN_HELP.readYourPlan} />
            </div>
            {mine?.stale ? (
                <p className="text-sm font-semibold text-blue-900" data-testid="plan-read-stale">
                    The office changed the plan after you read it. Read it again.
                </p>
            ) : mine?.verdict === "looks_wrong" ? (
                <p className="text-sm text-red-800" data-testid="plan-read-flagged">
                    You told the office this looks wrong · {readTimeWords(mine.created_at)}. {callOffice}
                </p>
            ) : (
                <p className="text-sm text-stone-700">
                    Read it before you leave the shop. Not sure? Ask now, not at the site.
                </p>
            )}
            {wrongOpen ? (
                <div className="space-y-2" data-testid="plan-wrong-form">
                    <label htmlFor={`plan-wrong-${jobId}`} className="block text-sm font-semibold">
                        What looks wrong? <span className="font-normal text-stone-500">(you can leave it blank)</span>
                    </label>
                    <textarea
                        id={`plan-wrong-${jobId}`}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={3}
                        placeholder={'e.g. The truck has a 6" replacement on it; the plan says repack.'}
                        className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900"
                    />
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            className="min-h-[48px] flex-1 rounded-lg bg-red-700 px-4 text-base font-bold text-white disabled:opacity-50"
                            disabled={send.isPending}
                            onClick={() => send.mutate({ verdict: "looks_wrong", note: note.trim() || undefined })}
                        >
                            Send to the office
                        </button>
                        <button
                            type="button"
                            className="min-h-[48px] rounded-lg border border-stone-300 px-4 text-base font-semibold"
                            onClick={() => setWrongOpen(false)}
                        >
                            Back
                        </button>
                    </div>
                    <p className="text-sm">Then call them so you know before you go. {callOffice}</p>
                </div>
            ) : (
                <div className="grid gap-2">
                    <button
                        type="button"
                        className="min-h-[56px] w-full rounded-lg bg-blue-700 px-4 text-lg font-extrabold text-white disabled:opacity-50"
                        disabled={send.isPending}
                        onClick={() => send.mutate({ verdict: "got_it" })}
                        data-testid="plan-got-it"
                    >
                        Got it
                    </button>
                    <button
                        type="button"
                        className={cn(
                            "min-h-[48px] w-full rounded-lg border-2 border-red-700 bg-white px-4 text-base font-bold text-red-800",
                        )}
                        onClick={() => setWrongOpen(true)}
                        data-testid="plan-looks-wrong"
                    >
                        This looks wrong · call the office
                    </button>
                </div>
            )}
        </section>
    );
}

/** The office's side: who read the plan, on "What the tech will do". */
export function PlanReadStamps({ jobId }: { jobId: string }) {
    const q = usePlanReads(jobId);
    if (q.isLoading || q.isError || !q.data) return null;
    const reads = q.data.reads;
    return (
        <div className="space-y-1.5" data-testid="plan-read-stamps">
            <div className="flex flex-wrap items-center gap-1.5">
                {reads.length === 0 ? (
                    <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground">
                        No tech has read it yet
                    </span>
                ) : (
                    reads.map((r) => (
                        <span
                            key={r.user_id}
                            className={cn(
                                "rounded-full px-2.5 py-1 text-xs font-bold",
                                r.verdict === "looks_wrong"
                                    ? "bg-red-100 text-red-900 dark:bg-red-500/15 dark:text-red-200"
                                    : r.stale
                                      ? "bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200"
                                      : "bg-green-100 text-green-900 dark:bg-green-500/15 dark:text-green-200",
                            )}
                            data-testid={`plan-read-${r.verdict}`}
                        >
                            {r.verdict === "looks_wrong"
                                ? `${r.name ?? "Tech"}: This looks wrong · ${readTimeWords(r.created_at)}`
                                : `Read by ${r.name ?? "tech"} · ${readTimeWords(r.created_at)}${r.stale ? " (before your last change)" : ""}`}
                        </span>
                    ))
                )}
                <InfoButton topic={TECH_PLAN_HELP.officeReads} />
            </div>
            {reads
                .filter((r) => r.verdict === "looks_wrong" && r.note)
                .map((r) => (
                    <p key={`${r.user_id}-note`} className="text-sm text-red-900 dark:text-red-200">
                        {r.name ?? "Tech"} said: &ldquo;{r.note}&rdquo;
                    </p>
                ))}
        </div>
    );
}
